// src/components/UserDropdown.jsx
//
// Dropdown que se abre desde el footer del Sidebar (avatar + nombre + rol).
// 3 opciones + separador + Cerrar sesión:
//   - Mi cuenta            → abre MiCuentaModal
//   - Configuración        → abre ConfiguracionModal
//   - Ayuda y soporte      → mailto:contacto@edgeelite.cl
//   ────────────────────────
//   - Cerrar sesión        → useLogoutFlow().startLogout (splash 3s → /)
//
// Renderizado vía createPortal a document.body para escapar de overflow
// del sidebar y poder posicionarse libremente. En desktop: position
// fixed cerca del trigger. En mobile: sheet desde abajo (CSS).
//
// Click outside + ESC cierran. Pasamos el bounding rect del trigger
// para posicionar el dropdown a la derecha del avatar.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthContext';
import { useLogoutFlow } from '../auth/useLogoutFlow';
import SplashScreen from './SplashScreen';
import MiCuentaModal from './modals/MiCuentaModal';
import ConfiguracionModal from './modals/ConfiguracionModal';

// ── Iconos SVG inline (pattern de Topbar.jsx) ──────────────────────
const I = ({ children, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);
const IconUser = () => (
  <I><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></I>
);
const IconSettings = () => (
  <I>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </I>
);
const IconLogOut = () => (
  <I><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></I>
);

// ── Posicionamiento ────────────────────────────────────────────────
function getDesktopPosition(rect) {
  if (!rect) return { left: 16, top: 16 };
  // El sidebar está a la izquierda; el dropdown sale a la DERECHA del trigger
  // alineado al borde superior. Si no hay espacio, fallback a alinear bottom.
  const left = rect.right + 8;
  const top = Math.min(rect.top, window.innerHeight - 320);
  return { left: Math.max(8, left), top: Math.max(8, top) };
}

export default function UserDropdown({ open, triggerRect, onClose, onTriggerClick }) {
  const { user, userData } = useAuth();
  const { showLogoutSplash, startLogout, handleLogoutComplete } = useLogoutFlow();
  const [showMiCuenta, setShowMiCuenta] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const dropdownRef = useRef(null);

  // ESC cierra
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Click outside cierra (excluye el trigger porque su click ya togglea)
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      // Si el click cae en el trigger del Sidebar, dejá que el trigger maneje el toggle.
      if (e.target.closest('[data-userdropdown-trigger]')) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, onClose]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const pos = isMobile ? null : getDesktopPosition(triggerRect);

  const nombre =
    userData?.nombre ||
    user?.displayName ||
    (user?.email ? user.email.split('@')[0] : 'Usuario');
  const email = userData?.email || user?.email || '';

  const handleLogout = () => {
    onClose?.();
    startLogout();
  };

  // Splash de logout (early return para que el Sidebar y dropdown no
  // sigan visibles bajo el splash naranja).
  if (showLogoutSplash) {
    return createPortal(
      <SplashScreen type="logout" onComplete={handleLogoutComplete} />,
      document.body
    );
  }

  return (
    <>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="dm-dropdown"
            role="menu"
            aria-label="Menú de usuario"
            style={pos ? { left: pos.left, top: pos.top } : undefined}
          >
            <div className="dm-dropdown-header">
              <div className="name">{nombre}</div>
              {email && <div className="email">{email}</div>}
            </div>

            <button
              className="dm-dropdown-item"
              role="menuitem"
              onClick={() => { onClose?.(); setShowMiCuenta(true); }}
              type="button"
            >
              <span className="ico"><IconUser /></span>
              Mi cuenta
            </button>

            <button
              className="dm-dropdown-item"
              role="menuitem"
              onClick={() => { onClose?.(); setShowConfig(true); }}
              type="button"
            >
              <span className="ico"><IconSettings /></span>
              Configuración
            </button>

            <div className="dm-dropdown-divider" />

            <button
              className="dm-dropdown-item danger"
              role="menuitem"
              onClick={handleLogout}
              type="button"
            >
              <span className="ico"><IconLogOut /></span>
              Cerrar sesión
            </button>
          </div>,
          document.body
        )}

      {/* Modals viven fuera del dropdown — siguen visibles aunque el
          dropdown haya cerrado al click. */}
      <MiCuentaModal open={showMiCuenta} onClose={() => setShowMiCuenta(false)} />
      <ConfiguracionModal open={showConfig} onClose={() => setShowConfig(false)} />
    </>
  );
}
